/* F1B.3B explicit contract seam. No browser listeners, Shopify transport or production activation. */
(function (root, factory) {
  'use strict';
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else if (!root.NikuyaCartManagedCoordinator) root.NikuyaCartManagedCoordinator = factory();
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';
  const clone = v => JSON.parse(JSON.stringify(v));
  function create(options = {}) {
    const {binding, sessionId, caseSku, ledger, store, canPersist, locks, transport, plan, authorize, timeoutMs = 5000} = options;
    let generation = 0;
    const stop = reason => ({status:'BLOCKED',reason,writes:0,retryAutomatically:false});
    const cartKey = 'nikuya:managed:v1:' + binding;
    const receiptKey = cartKey + ':' + caseSku;
    const pendingKey = cartKey + ':pending';
    const permitted = () => typeof canPersist === 'function' && canPersist() === true;
    async function get(key) { if (!permitted()) throw Error('STORAGE_NOT_APPROVED'); return bounded(()=>store.get(key)); }
    async function set(key, value) { if (!permitted()) throw Error('STORAGE_NOT_APPROVED'); await bounded(()=>store.set(key, value)); }
    async function bounded(fn) {
      let timer;
      try { return await Promise.race([Promise.resolve().then(fn), new Promise((_, reject) => { timer=setTimeout(()=>reject(Error('TIMEOUT')),timeoutMs); })]); }
      finally { clearTimeout(timer); }
    }
    async function run({trigger, signal} = {}) {
      // The public storefront cannot configure true gates or provide an automatic trigger.
      if (options.mode !== 'CONTRACT_TEST_ONLY' && !(options.mode === 'AUTHORIZED_RUNTIME' && options.isAuthorized?.() === true)) return stop('PRODUCTION_EXECUTION_DISABLED');
      if (trigger !== 'VERIFIED_COMMERCE_REQUEST') return stop('NON_COMMERCE_TRIGGER');
      if (!/^[a-f0-9]{64}$/.test(binding || '') || !/^NKY\d+$/.test(caseSku || '') ||
          !Number.isSafeInteger(timeoutMs) || timeoutMs < 1 || timeoutMs > 30000 ||
          !ledger || !store || store.shared !== true || !locks || typeof locks.request !== 'function' ||
          !transport || typeof plan !== 'function' || typeof authorize !== 'function') return stop('SAFE_SHARED_COORDINATION_REQUIRED');
      const epoch = generation;
      try {
        const fence = options.mutationFence;
        if (fence && (typeof fence.runManaged !== 'function' || fence.lockName(binding) !== cartKey)) return stop('SHARED_NATIVE_FENCE_REQUIRED');
        const withLock = task => fence ? fence.runManaged({binding, signal, task}) : locks.request(cartKey, {mode:'exclusive', ...(signal ? {signal} : {})}, task);
        return await withLock(async () => {
          if (signal?.aborted || epoch !== generation || (options.mode === 'AUTHORIZED_RUNTIME' && options.isAuthorized?.() !== true)) return stop('INVALIDATED');
          // Always read after acquiring the cart-wide lock, including blocked/second callers.
          const before = clone(await bounded(()=>transport.read()));
          if (before.authoritative !== true) return stop('AUTHORITATIVE_READ_REQUIRED');
          if (before.binding !== binding) {
            await set(pendingKey,{v:1,binding,sessionId,state:'CART_BINDING_CHANGED'});
            return stop('CART_BINDING_CHANGED');
          }
          if (await get(cartKey + ':session') !== sessionId || typeof sessionId !== 'string' || !sessionId) return stop('SESSION_UNRESOLVED');
          if (await get(pendingKey) != null) return stop('UNRESOLVED_WRITE');
          let receipt = await get(receiptKey);
          const owned = ledger.verifyFamily(receipt, before);
          if (receipt != null && owned.status !== 'VERIFIED') {
            await set(pendingKey,{v:1,binding,sessionId,state:'OWNERSHIP_UNRESOLVED'});
            return stop('OWNERSHIP_UNRESOLVED');
          }
          if (receipt == null && ledger.hasUnresolvedCase(before)) return stop('RECEIPT_REQUIRED');
          // Caller-supplied prepared requests are not accepted; only an in-lock fresh plan is used.
          const prepared = await plan(clone(before), owned.keys);
          if (prepared?.snapshotRevision !== before.revision || await authorize(clone(before), clone(prepared)) !== true) return stop('FRESH_AUTHORITY_REQUIRED');
          if (signal?.aborted || epoch !== generation || (options.mode === 'AUTHORIZED_RUNTIME' && options.isAuthorized?.() !== true)) return stop('INVALIDATED');
          if (prepared.status === 'NO_OP') return {status:'NO_OP',writes:0,retryAutomatically:false};
          if (prepared.status !== 'PREPARED_NOT_EXECUTED' || !ledger.allowsRequest(receipt,before,prepared.request)) return stop('PLAN_BLOCKED');
          // Durable uncertainty record BEFORE dispatch survives page termination and lock release.
          // Never expire or automatically clear this record: timeout is not cancellation.
          await set(pendingKey,{v:1,binding,sessionId,state:'PENDING'});
          if (signal?.aborted || epoch !== generation || !permitted() || (options.mode === 'AUTHORIZED_RUNTIME' && options.isAuthorized?.() !== true)) return stop('INVALIDATED_BEFORE_DISPATCH');
          let ack, uncertain = false, after;
          try { ack = await bounded(()=>transport.write(clone(prepared.request), {snapshotRevision:before.revision})); if (ack?.ok !== true) uncertain = true; }
          catch (_) { uncertain = true; }
          try { after = clone(await bounded(()=>transport.read())); }
          catch (_) { return {status:'UNCERTAIN',reason:'POST_READ_UNAVAILABLE',writes:1,retryAutomatically:false}; }
          if (uncertain) {
            // A reread can show the intended quantity without establishing acknowledgement/provenance.
            let observedAchieved = false;
            try { observedAchieved = await authorize(clone(after), {status:'OBSERVE_ONLY',request:clone(prepared.request)}) === true; } catch (_) { /* Keep uncertainty. */ }
            return {status:observedAchieved ? 'OBSERVED_ACHIEVED' : 'UNCERTAIN',observedAchieved,
              reason:'WRITE_UNCERTAIN_RECEIPT_NOT_ADVANCED',writes:1,retryAutomatically:false};
          }
          try {
            receipt = ledger.advanceReceipt({receipt,before,after,request:prepared.request,acknowledgement:ack});
            if (await authorize(clone(after), {status:'VERIFY_AFTER',request:clone(prepared.request)}) !== true) throw Error('POST_AUTHORITY_INVALID');
            await set(receiptKey,receipt);
            await set(pendingKey,null);
          } catch (_) { return {status:'UNCERTAIN',reason:'POST_WRITE_PROVENANCE_UNRESOLVED',writes:1,retryAutomatically:false}; }
          return {status:'CONFIRMED',writes:1,retryAutomatically:false};
        });
      } catch (_) { return stop('COORDINATION_OR_STORAGE_UNAVAILABLE'); }
    }
    return Object.freeze({run, invalidate:()=>{generation++;}, gates:()=>Object.freeze({financialEnforcementVerified:false,casePricingVerified:false,caseReconciliationEnabled:false})});
  }
  // Explicit opt-in shared storage seam; no fallback to cookies/localStorage or acquisition memory.
  function createIndexedDBStore({indexedDB, canPersist, databaseName = 'nikuya-managed-contract-v1', expireRecords = false, clock = Date.now} = {}) {
    async function operation(key, value, write) {
      if (typeof canPersist !== 'function' || canPersist() !== true || !indexedDB) throw Error('STORAGE_NOT_APPROVED');
      const db = await new Promise((resolve,reject)=>{
        const request=indexedDB.open(databaseName,1);
        request.onupgradeneeded=()=>request.result.createObjectStore('records');
        request.onsuccess=()=>resolve(request.result);request.onerror=()=>reject(Error('STORAGE_UNAVAILABLE'));
        request.onblocked=()=>reject(Error('STORAGE_BLOCKED'));
      });
      try {
        if (canPersist() !== true) throw Error('STORAGE_NOT_APPROVED');
        return await new Promise((resolve,reject)=>{
          const tx=db.transaction('records',write||expireRecords?'readwrite':'readonly'), records=tx.objectStore('records');
          let request;
          const perform=()=>{request=write ? (value === null ? records.delete(key) : records.put(clone(value),key)) : records.get(key)};
          if(expireRecords){
            const all=records.openCursor(),entries=new Map();
            all.onsuccess=()=>{
              const cursor=all.result;
              if(cursor){entries.set(cursor.key,cursor.value);cursor.continue();return}
              if(canPersist()!==true){tx.abort();return}
              const now=clock(),pending=[...entries].some(([k,v])=>v!=null&&(k.endsWith(':pending')||k.endsWith(':bootstrap-pending')||k==='nikuya:managed:v1:native-unbound'));
              if(!pending&&Number.isSafeInteger(now))for(const [k,r] of entries){
                if(!Number.isSafeInteger(r?.expiresAt)||now<r.expiresAt)continue;
                if(k==='nikuya:acquisition:v2'){
                  records.put({v:1,expired:true},k+':expired');records.delete(k);
                }else if(/^nikuya:managed:v1:[a-f0-9]{64}:(session-meta|NKY000100|NKY000101|NKY000129)$/.test(k)){
                  records.delete(k);
                  if(k.endsWith(':session-meta'))records.delete(k.slice(0,-5));
                }
              }
              perform();
            };
          }else perform();
          tx.oncomplete=()=>resolve(write ? undefined : request.result ?? null);
          tx.onerror=tx.onabort=()=>reject(Error('STORAGE_UNAVAILABLE'));
        });
      } finally { db.close(); }
    }
    return Object.freeze({shared:true,retentionManaged:expireRecords,get:key=>operation(key,null,false),set:(key,value)=>operation(key,value,true)});
  }
  return Object.freeze({create,createIndexedDBStore});
});
